import React, { useState, useEffect } from 'react';
import {
  Form,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Select,
  Container
} from '@cloudscape-design/components';

export interface CustomFieldComponentProps<T> {
  value: T;
  onChange: (value: T) => void;
  onBlur?: () => void;
  errorText?: string;
  ref?: React.Ref<any>;
  isSubmitted?: boolean;
  disabled: any
}

export interface BaseFormField<T = any> {
  name: string;
  label: string;
  type: 'text' | 'email' | 'select' | 'number' | 'select_dependent';
  isCustom: boolean;
  required?: boolean;
  disabled?: (formValues?: Record<string, any>) => boolean;
  validation?: (value: T, formValues?: Record<string, any>) => string | undefined;  // Updated validation signature
  customComponent?: React.ComponentType<CustomFieldComponentProps<T>>;
  description?: string;
  placeholder?: string;
  rawOptions?: Array<{ label: string; value: any; filterValue?: any}>;  // For select fields
  options?: Array<{ label: string; value: any; filterValue?: any}>;  // For select fields
  dependentOn?: {
    field: string;    // Name of parent field
  };
  onChange?: (value: T, formValues: Record<string, any>) => void;  // Add onChange callback
  onBlur?: (value: T, formValues: Record<string, any>) => void;    // Add onBlur callback
}

export interface BaseFormProps<T extends Record<string, any>> {
  fields: BaseFormField[];
  initialValues: T;
  onSubmit: (values: T) => Promise<void>;
  onCancel?: () => void;
  submitButtonText?: string;
  cancelButtonText?: string;
  onFieldChange?: (name: string, value: any, formValues: T) => void;  // Add field change callback
}

export function BaseCloudscapeForm<T extends Record<string, any>>({
  fields,
  initialValues,
  onSubmit,
  onCancel,
  submitButtonText = 'Submit',
  cancelButtonText = 'Cancel',
  onFieldChange
}: BaseFormProps<T>) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Reset form when initialValues change
  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const validateField = (field: BaseFormField, value: any): string | undefined => {
    if (field.required && (value === undefined || value === '' || 
        (Array.isArray(value) && value.length === 0))) {
      return `${field.label} is required`;
    }
    return field.validation?.(value, values);  // Pass the full form values
  };
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;
    
    fields.forEach(field => {
      const error = validateField(field, values[field.name]);
      if (error) {
        newErrors[field.name] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitted(true);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit(values);
    } catch (error) {
      console.error('Form submission error:', error);
      // Handle submission error if needed
    } finally {
      setIsLoading(false);
    }
  };


  const handleFieldChange = (name: string, value: any) => {
    setValues(prev => {
      let newValues = { ...prev, [name]: value } as T;
      
      // Reset dependent fields when parent changes
      fields.forEach(field => {
        if (field.dependentOn?.field === name){
          newValues = { ...newValues, [field.name]: null } as T;
        }
      });
      
      if (name === 'is_readonly_access' && value === true) {
        newValues = { ...newValues, policies: [] } as T;
      }
      
      // Call the field-specific onChange if provided
      const field = fields.find(f => f.name === name);
      if (field?.onChange) {
        field.onChange(value, newValues);
      }
      
      // Call the form-level onChange if provided
      if (onFieldChange) {
        onFieldChange(name, value, newValues);
      }
      
      return newValues;
    });
  };

  const handleFieldBlur = (name: string, value: any) => {
    const field = fields.find(f => f.name === name);
    if (field?.onBlur) {
      field.onBlur(value, values);
    }
  };
  
  const renderField = (field: BaseFormField) => {
    const commonProps = {
      key: field.name,
      label: field.label,
      description: field.description,
      errorText: isSubmitted ? errors[field.name] : undefined,
      i18nStrings: { errorIconAriaLabel: 'Error' }
    };

    if (field.isCustom  && field.customComponent) {
      const CustomComponent = field.customComponent;
      return (
        <FormField {...commonProps}>
          <CustomComponent
            value={values[field.name]}
            onChange={(value) => handleFieldChange(field.name, value)}
            onBlur={() => handleFieldBlur(field.name, values[field.name])}
            errorText={errors[field.name]}
            isSubmitted={isSubmitted}
            disabled={field.disabled?.(values)}
          />
        </FormField>
      );
    }

    switch (field.type) {

    case 'select':
        // Handle dependent dropdown filtering
        let selectOptions = field.options || [];
        if (field.dependentOn?.field) {
          const parentField = field.dependentOn.field;
          const parentValue = values[parentField];
          if (parentValue && field.rawOptions) {
            selectOptions = field.rawOptions.filter(option => 
              option.filterValue === parentValue.value
            );
          } else {
            selectOptions = [];
          }
        }
        
        return (
            <FormField {...commonProps}>
            <Select
                selectedOption={values[field.name] || null}
                onChange={({ detail }) => 
                handleFieldChange(field.name, detail.selectedOption)
                }
                options={selectOptions}
                placeholder={field.placeholder}
                selectedAriaLabel="Selected"
                disabled={field.disabled?.(values)}
                filteringType="auto"
            />
            </FormField>
        );
        
        case 'select_dependent': {
          const parentField = field.dependentOn?.field;
          const parentValue = parentField ? values[parentField] : null;
          
          const filteredOptions = field.options?.filter(option => 
            parentValue ? option.filterValue === parentValue.value : false
          ) || [];
  
          return (
            <FormField {...commonProps}>
              <Select
                selectedOption={values[field.name] || null}
                onChange={({ detail }) => 
                  handleFieldChange(field.name, detail.selectedOption)
                }
                options={filteredOptions}
                placeholder={field.placeholder}
                disabled={!parentValue}
                selectedAriaLabel="Selected"
              />
            </FormField>
          );
        }
  

      case 'number':
        return (
          <FormField {...commonProps}>
            <Input
              type="number"
              value={values[field.name]?.toString() || ''}
              onChange={({ detail }) => 
                handleFieldChange(field.name, detail.value)
              }
              placeholder={field.placeholder}
              disabled={field.disabled?.(values)}
            />
          </FormField>
        );

      default:
        return (
          <FormField {...commonProps}>
            <Input
              type={field.type}
              value={values[field.name] || ''}
              onChange={({ detail }) => 
                handleFieldChange(field.name, detail.value)
              }
              placeholder={field.placeholder}
              disabled={field.disabled?.(values)}
            />
          </FormField>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            {onCancel && (
              <Button formAction="none" onClick={onCancel} variant="link">
                {cancelButtonText}
              </Button>
            )}
            <Button 
              formAction="submit" 
              variant="primary" 
              loading={isLoading}
            >
              {submitButtonText}
            </Button>
          </SpaceBetween>
        }
      >
        <Container>
          <SpaceBetween direction="vertical" size="l">
            {fields.map(renderField)}
          </SpaceBetween>
        </Container>
      </Form>
    </form>
  );
}
